import { useContext } from 'react'
import type { ComponentPropsWithoutRef } from 'react'
import { Maximize, X } from 'lucide-react'
import { toast } from 'sonner'
import {
  StreamdownContext,
  TableCopyDropdown,
  TableDownloadDropdown,
} from 'streamdown'
import type { ExtraProps } from 'streamdown'

import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog'
import { cn } from '@/lib/utils'

const COPY_LABELS = { md: 'Markdown', csv: 'CSV', tsv: 'TSV' } as const
const DOWNLOAD_LABELS = { csv: 'CSV', markdown: 'Markdown' } as const

// Module-level handlers: stable identities, nothing to memoize.
const handleCopy = (format: keyof typeof COPY_LABELS) =>
  toast.success(`Copied as ${COPY_LABELS[format]}`)
const handleCopyError = () => toast.error('Failed to copy')
const handleDownload = (format: keyof typeof DOWNLOAD_LABELS) =>
  toast.success(`Downloaded as ${DOWNLOAD_LABELS[format]}`)
const handleDownloadError = () => toast.error('Failed to download table')

// streamdown's own dropdowns, with the onCopy/onError callbacks the built-in
// table leaves unwired (which is what made every failure a silent no-op).
// Copy/download behavior stays streamdown's — text/plain + text/html clipboard
// flavors, UTF-8 BOM on CSV downloads — and they disable themselves during
// streaming via StreamdownContext.
function TableToolbar() {
  return (
    <>
      <TableCopyDropdown onCopy={handleCopy} onError={handleCopyError} />
      <TableDownloadDropdown
        onDownload={handleDownload}
        onError={handleDownloadError}
      />
    </>
  )
}

/**
 * Table renderer for chat markdown, replacing streamdown's built-in table.
 * Same DOM and classes as the built-in (so the `data-streamdown` CSS overrides
 * in index.css keep applying), but the toolbar wires streamdown's exported
 * dropdowns to toasts, and fullscreen is rebuilt on the app Dialog. The
 * dropdowns resolve their table via `closest('[data-streamdown=
 * "table-wrapper"]')`, so the fullscreen container is stamped `table-wrapper`
 * too — the built-in fullscreen portals its toolbar away from any wrapper,
 * leaving its copy/download buttons deterministically dead.
 */
export function ChatMarkdownTable({
  children,
  className,
  node: _node,
  ...props
}: ComponentPropsWithoutRef<'table'> & ExtraProps) {
  const { isAnimating } = useContext(StreamdownContext)

  return (
    <div
      className="my-4 flex flex-col gap-2 rounded-lg border border-border bg-sidebar p-2"
      data-streamdown="table-wrapper">
      <div className="flex items-center justify-end gap-1">
        <TableToolbar />
        <Dialog>
          <DialogTrigger asChild>
            <Button
              variant="ghost"
              size="icon-sm"
              className="size-auto rounded-none p-1 text-muted-foreground hover:bg-transparent hover:text-foreground"
              title="View fullscreen"
              aria-label="View fullscreen"
              disabled={isAnimating}>
              <Maximize />
            </Button>
          </DialogTrigger>
          <DialogContent
            showCloseButton={false}
            className="inset-0 top-0 left-0 flex max-w-none translate-x-0 translate-y-0 flex-col rounded-none p-0 sm:max-w-none"
            data-streamdown="table-fullscreen"
            aria-describedby={undefined}>
            <DialogTitle className="sr-only">Fullscreen table</DialogTitle>
            <div
              className="flex h-full flex-col"
              role="presentation"
              data-streamdown="table-wrapper">
              <div className="flex items-center justify-end gap-1 p-4">
                <TableToolbar />
                <DialogClose asChild>
                  <Button
                    variant="ghost"
                    size="icon-sm"
                    className="size-auto rounded-md p-1 text-muted-foreground hover:bg-muted hover:text-foreground"
                    title="Exit fullscreen"
                    aria-label="Exit fullscreen">
                    <X className="size-5" />
                  </Button>
                </DialogClose>
              </div>
              <div className="flex-1 overflow-auto p-4 pt-0 [&_thead]:sticky [&_thead]:top-0 [&_thead]:z-10">
                <table
                  className="w-full border-collapse border border-border"
                  data-streamdown="table"
                  {...props}>
                  {children}
                </table>
              </div>
            </div>
          </DialogContent>
        </Dialog>
      </div>
      <div className="border-collapse overflow-x-auto overflow-y-auto rounded-md border border-border bg-background">
        <table
          className={cn('w-full divide-y divide-border', className)}
          data-streamdown="table"
          {...props}>
          {children}
        </table>
      </div>
    </div>
  )
}
